import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Bind } from "../../decorators/bind.decorator";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import {
    EngagementService,
    EngagementSystemAlert,
    EngagementSystemAlertAudience,
} from "./engagement-metrics/engagement.service";
import { SeSecurityStore } from "./se.security.store";
import { AlertService } from "../../services/alert.service";
import { S25Util } from "../../util/s25-util";
import { S25DatepickerModule } from "../s25-datepicker/s25.datepicker.module";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25RichTextEditorModule } from "../s25-rich-text-editor/s25.rich.text.editor.module";

import { UserStore } from "../../stores/user.store";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Item } from "../../pojo/Item";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { GenericTableFadePreviewComponent } from "../s25-table/generics/generic.table.fade.preview.component";
import { GenericTableRenderHTMLComponent } from "../s25-table/generics/generic.table.render.html.component";
import { Table } from "../s25-table/Table";
import { S25TableModule } from "../s25-table/s25.table.module";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { S25PipesModule } from "../../pipes/s25.pipes.module";

/** One audience row in the form; picks hold numeric `itemId`s aligned with search criteria / API. */
type AudienceFormRow = {
    audienceType: "ORG_ROLE" | "ROLE" | "SEC_GROUP" | "RATING";
    organization: DropDownItem<number> | null;
    orgContactRole: DropDownItem<number> | null;
    securityGroup: DropDownItem<number> | null;
    orgRating: DropDownItem<number> | null;
};

function dropDownSeed(id: number, itemTypeId?: number): DropDownItem<number> {
    const seed: DropDownItem<number> = { itemId: id };
    if (itemTypeId != null) {
        seed.itemTypeId = itemTypeId;
    }
    return seed;
}

function audienceRowHasPayload(row: AudienceFormRow): boolean {
    switch (row.audienceType) {
        case "ORG_ROLE":
            return row.organization?.itemId != null && row.orgContactRole?.itemId != null;
        case "ROLE":
            return row.orgContactRole?.itemId != null;
        case "SEC_GROUP":
            return row.securityGroup?.itemId != null;
        case "RATING":
            return row.orgRating?.itemId != null;
        default:
            return false;
    }
}

function emptyAudienceRow(): AudienceFormRow {
    return {
        audienceType: "ROLE",
        organization: null,
        orgContactRole: null,
        securityGroup: null,
        orgRating: null,
    };
}

/** Normalize API schedule timestamps for date pickers: start → start of day, end → end of day (matches save payload). */
function scheduleDateForPicker(apiDateTime: string | Date | undefined, bound: "start" | "end"): Date | null {
    if (apiDateTime == null || apiDateTime === "") {
        return null;
    }
    const parsed = typeof apiDateTime === "string" ? S25Util.date.parse(apiDateTime) : apiDateTime;
    if (!parsed || !S25Util.date.isValid(parsed)) {
        return null;
    }
    return bound === "end" ? S25Util.date.toEndOfDay(parsed) : S25Util.date.toStartOfDay(parsed);
}

@TypeManagerDecorator("s25-ng-se-system-alerts-admin")
@Component({
    selector: "s25-ng-se-system-alerts-admin",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25DatepickerModule,
        S25DropdownPaginatedModule,
        S25RichTextEditorModule,
        S25PipesModule,
        S25TableModule,
    ],
    template: `
        @if (securityStore.canConfigureSystemAlerts()) {
            <div class="se-sys-alerts-admin c-padding--double">
                <h2>System alerts</h2>
                <p class="c-margin-bottom--double">
                    Target audiences use OR across all rows. Empty audience = everyone (subject to schedule and guest
                    settings). Omit both schedule dates to show always; one date means open-ended on that side.
                </p>

                @if (alertsListResource.error()) {
                    <p class="c-text-danger" role="alert">Could not load alerts.</p>
                } @else if (alertsListResource.isLoading()) {
                    <p>Loading…</p>
                } @else {
                    <div class="se-sys-alerts-admin__table-wrap c-margin-bottom--double">
                        <s25-ng-table
                            #alertsTable
                            [dataSource]="alertsTableSource"
                            [hasRefresh]="true"
                            [showHeaderWhenNoData]="true"
                            [noResultsMessage]="'No system alerts configured.'"
                            [unlimitedWidth]="true"
                        />
                    </div>
                    @if (!alertFormVisible()) {
                        <button
                            type="button"
                            class="aw-button aw-button--primary se-sys-alerts-admin__create-btn c-margin-bottom--double"
                            (click)="startCreateAlert()"
                        >
                            Create Alert
                        </button>
                    }
                }

                @if (alertFormVisible()) {
                    <h3>{{ editingId() ? "Edit alert" : "New alert" }}</h3>
                    <div class="se-sys-alerts-admin__grid">
                        <div class="se-sys-alerts-admin__field se-sys-alerts-admin__field--body">
                            <span class="se-sys-alerts-admin__label">Body</span>
                            <s25-ng-rich-text-editor
                                class="se-sys-alerts-admin__rte"
                                [modelValue]="formBodyHtml()"
                                (modelValueChange)="formBodyHtml.set($event)"
                                [defaultHeight]="220"
                            />
                            <span class="se-sys-alerts-admin__label c-margin-top--single">Rendered preview</span>
                            <div
                                class="se-sys-alerts-admin__body-preview"
                                [innerHTML]="formBodyHtml() | safeHTML"
                            ></div>
                        </div>
                        <label class="se-sys-alerts-admin__field">
                            <span class="se-sys-alerts-admin__label">Presentation</span>
                            <select
                                class="c-input se-sys-alerts-admin__ux-select"
                                [ngModel]="formUxType()"
                                (ngModelChange)="formUxType.set($event)"
                            >
                                <option value="MODAL">Modal</option>
                                <option value="BANNER">Banner</option>
                            </select>
                        </label>
                        <label class="se-sys-alerts-admin__field se-sys-alerts-admin__field--checkbox">
                            <input
                                type="checkbox"
                                [ngModel]="formShowToAnonymous()"
                                (ngModelChange)="formShowToAnonymous.set($event)"
                            />
                            <span>Show to guests (not signed in)</span>
                        </label>
                        <label class="se-sys-alerts-admin__field se-sys-alerts-admin__field--checkbox">
                            <input type="checkbox" [ngModel]="formActive()" (ngModelChange)="formActive.set($event)" />
                            <span>Active</span>
                        </label>
                        <div class="se-sys-alerts-admin__field">
                            <span class="se-sys-alerts-admin__label">Start date (optional)</span>
                            <div class="se-sys-alerts-admin__date-row">
                                <s25-datepicker
                                    [inline]="true"
                                    [allowEmpty]="true"
                                    [autoSelectToday]="false"
                                    [value]="formStartDt()"
                                    (valueChange)="formStartDt.set($event)"
                                />
                                <button
                                    type="button"
                                    class="aw-button aw-button--outline aw-button--small"
                                    (click)="clearFormStartDt()"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div class="se-sys-alerts-admin__field">
                            <span class="se-sys-alerts-admin__label">End date (optional)</span>
                            <div class="se-sys-alerts-admin__date-row">
                                <s25-datepicker
                                    [inline]="true"
                                    [allowEmpty]="true"
                                    [autoSelectToday]="false"
                                    [value]="formEndDt()"
                                    (valueChange)="formEndDt.set($event)"
                                />
                                <button
                                    type="button"
                                    class="aw-button aw-button--outline aw-button--small"
                                    (click)="clearFormEndDt()"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="se-sys-alerts-admin__audience c-margin-top--double">
                        <h4>Audience rows (optional)</h4>
                        @for (
                            audienceRow of audienceRows();
                            track audienceTrackKey($index, audienceRow);
                            let idx = $index
                        ) {
                            <div class="se-sys-alerts-admin__audience-row">
                                <select
                                    class="c-input se-sys-alerts-admin__audience-type"
                                    [ngModel]="audienceRow.audienceType"
                                    (ngModelChange)="onAudienceTypeChange(idx, $event)"
                                >
                                    <option value="ORG_ROLE">Organization + contact role</option>
                                    <option value="ROLE">Contact role (any org)</option>
                                    <option value="SEC_GROUP">Security group</option>
                                    <option value="RATING">Organization status</option>
                                </select>

                                @switch (audienceRow.audienceType) {
                                    @case ("ORG_ROLE") {
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input"
                                            type="organizations"
                                            placeholder="Organization"
                                            [value]="audienceRow.organization"
                                            (valueChange)="patchAudienceRow(idx, 'organization', $event)"
                                        />
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input"
                                            type="organizationRoles"
                                            placeholder="Contact role"
                                            [value]="audienceRow.orgContactRole"
                                            (valueChange)="patchAudienceRow(idx, 'orgContactRole', $event)"
                                        />
                                    }
                                    @case ("ROLE") {
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input"
                                            type="organizationRoles"
                                            placeholder="Contact role"
                                            [value]="audienceRow.orgContactRole"
                                            (valueChange)="patchAudienceRow(idx, 'orgContactRole', $event)"
                                        />
                                    }
                                    @case ("SEC_GROUP") {
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input"
                                            type="securityGroups"
                                            placeholder="Security group"
                                            [value]="audienceRow.securityGroup"
                                            (valueChange)="patchAudienceRow(idx, 'securityGroup', $event)"
                                        />
                                    }
                                    @case ("RATING") {
                                        <s25-ng-dropdown-search-criteria
                                            class="c-input"
                                            type="organizationRatings"
                                            placeholder="Organization status"
                                            [value]="audienceRow.orgRating"
                                            (valueChange)="patchAudienceRow(idx, 'orgRating', $event)"
                                        />
                                    }
                                }

                                <button type="button" class="c-textButton" (click)="removeAudienceRow(idx)">
                                    Remove
                                </button>
                            </div>
                        }
                        <button type="button" class="c-textButton" (click)="addAudienceRow()">Add audience row</button>
                    </div>

                    <div class="c-margin-top--double se-sys-alerts-admin__submit">
                        <button
                            type="button"
                            class="aw-button aw-button--primary"
                            (click)="save()"
                            [disabled]="saving()"
                        >
                            {{ editingId() ? "Save changes" : "Create Alert" }}
                        </button>
                        <button
                            type="button"
                            class="aw-button aw-button--danger--outline c-margin-left--single"
                            (click)="resetForm()"
                        >
                            Cancel
                        </button>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .se-sys-alerts-admin h2 {
            margin-bottom: 1rem;
            font-size: 1.25rem;
            font-weight: 600;
            color: #333;
        }
        .se-sys-alerts-admin__grid {
            display: grid;
            gap: 1rem;
            max-width: 52rem;
        }
        .se-sys-alerts-admin__field {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            align-items: flex-start;
        }
        .se-sys-alerts-admin__field--body {
            align-self: stretch;
            width: 100%;
        }
        .se-sys-alerts-admin__label {
            font-weight: 600;
        }
        .se-sys-alerts-admin__field--checkbox {
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
        }
        .se-sys-alerts-admin__ux-select {
            width: auto;
            min-width: 9rem;
            max-width: 14rem;
        }
        .se-sys-alerts-admin__rte {
            display: block;
            width: 100%;
        }
        :host ::ng-deep .se-sys-alerts-admin__rte .tox-tinymce {
            width: 100%;
        }
        .se-sys-alerts-admin__date-row {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem;
        }
        .se-sys-alerts-admin__audience-row {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: flex-start;
            margin-bottom: 0.75rem;
        }
        .se-sys-alerts-admin__audience-type {
            min-width: 11rem;
        }
        .se-sys-alerts-admin__body-preview {
            border: 1px dashed;
            padding: 0.5em;
            width: 100%;
            max-width: 52rem;
            box-sizing: border-box;
        }
    `,
})
export class SeSystemAlertsAdminComponent {
    @ViewChild("alertsTable") private alertsTable?: S25TableComponent;

    protected readonly securityStore = inject(SeSecurityStore);
    private readonly userStore = inject(UserStore);

    readonly alertsListResource = s25Resource({
        if: () => this.securityStore.canConfigureSystemAlerts(),
        loader: () => EngagementService.listSystemAlertsAdmin(),
    });

    readonly sortedAlerts = computed(() => {
        const list = this.alertsListResource.value() ?? [];
        return [...list].sort((first, second) => (first.sortOrder ?? 0) - (second.sortOrder ?? 0));
    });

    readonly alertsTableSource: Table.DataSource = {
        type: "unpaginated",
        dataSource: this.getAlertsTableRows,
        columns: [
            { id: "sortOrder", header: "Order", minWidth: 72 },
            { id: "uxType", header: "UX", minWidth: 88 },
            { id: "active", header: "Active", minWidth: 72 },
            { id: "startDt", header: "Start", minWidth: 110 },
            { id: "endDt", header: "End", minWidth: 110 },
            {
                id: "bodyPreview",
                header: "Body",
                minWidth: 200,
                cells: { component: GenericTableFadePreviewComponent },
            },
            GenericTableButtonComponent.Column("Edit", this.onAlertsTableEdit, "outline"),
            GenericTableButtonComponent.Column("Up", this.onAlertsTableMoveUp, "outline"),
            GenericTableButtonComponent.Column("Down", this.onAlertsTableMoveDown, "outline"),
            GenericTableButtonComponent.Column("Delete", this.onAlertsTableDelete, "danger--outline"),
        ],
    };

    readonly editingId = signal<number | undefined>(undefined);
    readonly saving = signal(false);
    /** When false, only the list (+ Create Alert) is shown; Edit / Create open the same form panel. */
    readonly alertFormVisible = signal(false);

    readonly formBodyHtml = signal("");
    readonly formUxType = signal<"MODAL" | "BANNER">("MODAL");
    readonly formShowToAnonymous = signal(false);
    readonly formActive = signal(true);
    readonly formStartDt = signal<Date | null>(null);
    readonly formEndDt = signal<Date | null>(null);

    readonly audienceRows = signal<AudienceFormRow[]>([]);

    audienceTrackKey(index: number, row: AudienceFormRow): string {
        return `${index}-${row.audienceType}`;
    }

    patchAudienceRow<K extends keyof AudienceFormRow>(rowIndex: number, key: K, value: AudienceFormRow[K]): void {
        this.audienceRows.update((rows) => {
            const next = [...rows];
            const current = next[rowIndex];
            if (!current) {
                return rows;
            }
            next[rowIndex] = { ...current, [key]: value };
            return next;
        });
    }

    onAudienceTypeChange(rowIndex: number, nextType: AudienceFormRow["audienceType"]): void {
        this.audienceRows.update((rows) => {
            const next = [...rows];
            const previous = next[rowIndex];
            if (!previous) {
                return rows;
            }
            const cleared: AudienceFormRow = {
                audienceType: nextType,
                organization: nextType === "ORG_ROLE" ? previous.organization : null,
                orgContactRole: nextType === "ORG_ROLE" || nextType === "ROLE" ? previous.orgContactRole : null,
                securityGroup: nextType === "SEC_GROUP" ? previous.securityGroup : null,
                orgRating: nextType === "RATING" ? previous.orgRating : null,
            };
            next[rowIndex] = cleared;
            return next;
        });
    }

    addAudienceRow(): void {
        this.audienceRows.update((rows) => [...rows, emptyAudienceRow()]);
    }

    removeAudienceRow(index: number): void {
        this.audienceRows.update((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
    }

    clearFormStartDt(): void {
        this.formStartDt.set(null);
    }

    clearFormEndDt(): void {
        this.formEndDt.set(null);
    }

    /** Schedule bounds as stored on the server (ISO or Date); shown with the signed-in user's date preference. */
    scheduleBoundsDisplay(isoDateTime: string | Date | undefined): string {
        if (isoDateTime == null || isoDateTime === "") {
            return "";
        }
        const parsed = typeof isoDateTime === "string" ? new Date(isoDateTime) : isoDateTime;
        if (Number.isNaN(parsed.getTime())) {
            return typeof isoDateTime === "string" && isoDateTime.length >= 10
                ? isoDateTime.slice(0, 10)
                : typeof isoDateTime === "string"
                  ? isoDateTime
                  : "";
        }
        const formatString = this.userStore.dateFormat();
        if (!formatString) {
            if (typeof isoDateTime === "string") {
                return isoDateTime.length >= 10 ? isoDateTime.slice(0, 10) : isoDateTime;
            }
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, "0");
            const dayOfMonth = String(parsed.getDate()).padStart(2, "0");
            return `${year}-${month}-${dayOfMonth}`;
        }
        const fallbackLocal =
            typeof isoDateTime === "string"
                ? isoDateTime.length >= 10
                    ? isoDateTime.slice(0, 10)
                    : isoDateTime
                : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
        return S25Datefilter.transform(parsed, formatString) ?? fallbackLocal;
    }

    @Bind
    async getAlertsTableRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) {
            await this.alertsListResource.reloadAndAwait();
        }
        const alerts = this.sortedAlerts();
        return {
            rows: alerts.map((doc) => ({
                id: doc.engagementSystemAlertId!,
                name:
                    doc.bodyHtml?.trim()?.slice(0, 48)?.replace(/\s+/g, " ") || `Alert ${doc.engagementSystemAlertId}`,
                data: { doc },
                cells: {
                    sortOrder: { text: doc.sortOrder ?? "" },
                    uxType: { text: doc.uxType ?? "" },
                    active: { text: doc.active !== false ? "Yes" : "No" },
                    startDt: { text: this.scheduleBoundsDisplay(doc.startDt) },
                    endDt: { text: this.scheduleBoundsDisplay(doc.endDt) },
                    bodyPreview: {
                        textValue: doc.bodyHtml,
                        inputs: {
                            cell: {
                                component: GenericTableRenderHTMLComponent,
                                inputs: { text: doc.bodyHtml ?? "", isHtml: true },
                            },
                        },
                    },
                },
            })),
        };
    }

    @Bind
    async onAlertsTableEdit(row: Table.Row): Promise<void> {
        this.editAlert(row.data.doc as EngagementSystemAlert);
    }

    @Bind
    async onAlertsTableMoveUp(row: Table.Row): Promise<void> {
        const doc = row.data.doc as EngagementSystemAlert;
        await this.moveAlert(doc.engagementSystemAlertId!, "up");
    }

    @Bind
    async onAlertsTableMoveDown(row: Table.Row): Promise<void> {
        const doc = row.data.doc as EngagementSystemAlert;
        await this.moveAlert(doc.engagementSystemAlertId!, "down");
    }

    @Bind
    async onAlertsTableDelete(row: Table.Row): Promise<void> {
        await this.confirmDelete(row.data.doc as EngagementSystemAlert);
    }

    editAlert(doc: EngagementSystemAlert): void {
        this.editingId.set(doc.engagementSystemAlertId);
        this.formBodyHtml.set(doc.bodyHtml ?? "");
        this.formUxType.set(doc.uxType === "BANNER" ? "BANNER" : "MODAL");
        this.formShowToAnonymous.set(!!doc.showToAnonymous);
        this.formActive.set(doc.active !== false);
        this.formStartDt.set(scheduleDateForPicker(doc.startDt, "start"));
        this.formEndDt.set(scheduleDateForPicker(doc.endDt, "end"));
        const mappedRows: AudienceFormRow[] =
            doc.audiences?.map((criterion) => ({
                audienceType: criterion.audienceType ?? "ROLE",
                organization:
                    criterion.organizationId != null
                        ? dropDownSeed(criterion.organizationId, Item.Ids.Organization)
                        : null,
                orgContactRole: criterion.roleId != null ? dropDownSeed(criterion.roleId) : null,
                securityGroup: criterion.groupId != null ? dropDownSeed(criterion.groupId) : null,
                orgRating: criterion.ratingId != null ? dropDownSeed(criterion.ratingId) : null,
            })) ?? [];
        this.audienceRows.set(mappedRows);
        this.alertFormVisible.set(true);
    }

    /** Reset fields and hide the form (after save, Cancel, or when closing edit). */
    resetForm(): void {
        this.clearFormFields();
        this.alertFormVisible.set(false);
    }

    /** Open the form for a new alert with default field values. */
    startCreateAlert(): void {
        this.clearFormFields();
        this.alertFormVisible.set(true);
    }

    private clearFormFields(): void {
        this.editingId.set(undefined);
        this.formBodyHtml.set("");
        this.formUxType.set("MODAL");
        this.formShowToAnonymous.set(false);
        this.formActive.set(true);
        this.formStartDt.set(null);
        this.formEndDt.set(null);
        this.audienceRows.set([]);
    }

    private buildAlertFromForm(): EngagementSystemAlert {
        const audiences: EngagementSystemAlertAudience[] = this.audienceRows()
            .filter(audienceRowHasPayload)
            .map((row) => {
                const criterion: EngagementSystemAlertAudience = {
                    audienceType: row.audienceType,
                };
                const organizationId = row.organization?.itemId;
                const roleId = row.orgContactRole?.itemId;
                const groupId = row.securityGroup?.itemId;
                const ratingId = row.orgRating?.itemId;
                if (organizationId != null) {
                    criterion.organizationId = organizationId;
                }
                if (roleId != null) {
                    criterion.roleId = roleId;
                }
                if (groupId != null) {
                    criterion.groupId = groupId;
                }
                if (ratingId != null) {
                    criterion.ratingId = ratingId;
                }
                return criterion;
            });

        const startDtValue = this.formStartDt();
        const endDtValue = this.formEndDt();
        const alertPayload: EngagementSystemAlert = {
            bodyHtml: this.formBodyHtml() ?? "",
            uxType: this.formUxType(),
            showToAnonymous: this.formShowToAnonymous(),
            active: this.formActive(),
            audiences,
        };

        const hasStart = startDtValue != null && S25Util.date.isValid(startDtValue);
        const hasEnd = endDtValue != null && S25Util.date.isValid(endDtValue);
        if (hasStart && hasEnd) {
            const startDt = S25Util.date.toStartOfDay(startDtValue);
            const endDt = S25Util.date.toEndOfDay(endDtValue);
            if (!startDt || !endDt || !S25Util.date.isValid(startDt) || !S25Util.date.isValid(endDt)) {
                throw new Error("Expected valid start and end dates");
            }
            alertPayload.startDt = startDt;
            alertPayload.endDt = endDt;
        } else if (hasStart) {
            const startDt = S25Util.date.toStartOfDay(startDtValue);
            if (!startDt || !S25Util.date.isValid(startDt)) {
                throw new Error("Expected valid start date");
            }
            alertPayload.startDt = startDt;
        } else if (hasEnd) {
            const endDt = S25Util.date.toEndOfDay(endDtValue);
            if (!endDt || !S25Util.date.isValid(endDt)) {
                throw new Error("Expected valid end date");
            }
            alertPayload.endDt = endDt;
        }

        return alertPayload;
    }

    /** Same path as the grid refresh control: `forceRefresh` reloads the list then rebuilds rows. */
    private async refreshAlertsTable(): Promise<void> {
        if (this.alertsTable) {
            await this.alertsTable.refresh(true);
        } else {
            await this.alertsListResource.reloadAndAwait();
        }
    }

    async save(): Promise<void> {
        if (this.saving()) return;
        this.saving.set(true);
        try {
            const payload = this.buildAlertFromForm();
            const editingTargetId = this.editingId();
            if (editingTargetId != null) {
                payload.engagementSystemAlertId = editingTargetId;
                await EngagementService.updateSystemAlert(editingTargetId, payload);
            } else {
                await EngagementService.createSystemAlert(payload);
            }
            this.resetForm();
            await this.refreshAlertsTable();
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        } finally {
            this.saving.set(false);
        }
    }

    async moveAlert(alertId: number, direction: "up" | "down"): Promise<void> {
        try {
            await EngagementService.reorderSystemAlert(alertId, direction);
            this.applySystemAlertReorderToLocalList(alertId, direction);
            await this.alertsTable?.refresh(false);
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        }
    }

    /**
     * After a successful reorder API call, swap `sortOrder` on the two adjacent alerts so the grid
     * and any later edit payload match the server — without reloading the list from the network.
     */
    private applySystemAlertReorderToLocalList(alertId: number, direction: "up" | "down"): void {
        this.alertsListResource.resource.update((list) => {
            if (!list?.length) {
                return list;
            }
            const bySortOrder = (first: EngagementSystemAlert, second: EngagementSystemAlert) =>
                (first.sortOrder ?? 0) - (second.sortOrder ?? 0);
            const ordered = [...list].sort(bySortOrder);
            const movingIndex = ordered.findIndex((doc) => doc.engagementSystemAlertId === alertId);
            if (movingIndex < 0) {
                return list;
            }
            const neighborIndex = direction === "up" ? movingIndex - 1 : movingIndex + 1;
            if (neighborIndex < 0 || neighborIndex >= ordered.length) {
                return list;
            }
            const movingDoc = ordered[movingIndex];
            const neighborDoc = ordered[neighborIndex];
            const previousMovingSortOrder = movingDoc.sortOrder;
            movingDoc.sortOrder = neighborDoc.sortOrder;
            neighborDoc.sortOrder = previousMovingSortOrder;
            return [...list];
        });
    }

    async confirmDelete(doc: EngagementSystemAlert): Promise<void> {
        const confirmed = await AlertService.confirm("Delete alert? This cannot be undone.");
        if (!confirmed) return;
        try {
            await EngagementService.deleteSystemAlert(doc.engagementSystemAlertId!);
            if (this.editingId() === doc.engagementSystemAlertId) {
                this.resetForm();
            }
            await this.refreshAlertsTable();
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        }
    }
}
